import {
  Column,
  PaginationHook,
  TableColumnSortOrder,
  TableWithPagination,
  useTableHeadCell,
} from "@shared/primitive-components"
import { buildHrefLinkForRows } from "@shared/utils/helpers/buildHrefLinkForRows"
import { useCallback, useMemo } from "react"
import { Trip, tripSortKeyFeValues, TripSortKeysFE } from "../../../api/insights"
import { PATHS } from "../../../const/paths"
import { CompletedTripsData, makeTripData } from "../../handlers"
import {
  ChevronFakeLinkCell,
  SpeedingBreakdownCell,
  TripCompletedCell,
  VehicleCell,
  VehicleScoreCell,
} from "./tableCells"
import {
  safeMilesHeaderProps,
  speedingBreakdownHeaderProps,
  tripCompletedHeaderProps,
  unsafeMilesHeaderProps,
  vehicleHeaderProps,
  vehicleScoreHeaderProps,
} from "./tripTableHeaderCells"

export const COMPLETED_TRIPS_TABLE_DTI = {
  SCORE_TITLE: `TRIPS_TABLE_TITLE`,
  DAY_DATE: `TRIPS_TABLE_DAY_DATE`,
  TIME: `TRIPS_TABLE_TIME`,
  CHEVRON: `TRIPS_TABLE_CHEVRON`,
  SPEEDING: `TRIPS_TABLE_SPEEDING`,
  EXCEEDANCE: `TRIPS_TABLE_EXCEEDANCE`,
}

export type CompletedTripsTableProps = Readonly<{
  page: ReadonlyArray<Trip>
  dataCount: number
  pagination: PaginationHook
  sort: { property: TripSortKeysFE | undefined; order: TableColumnSortOrder | undefined } | undefined
  sortData: (property: TripSortKeysFE, newSort: TableColumnSortOrder | undefined) => void
  isVehicle?: boolean
  loading?: boolean
}>

export const CompletedTripsTable = ({
  page,
  sort,
  dataCount,
  pagination,
  isVehicle,
  sortData,
  loading,
}: CompletedTripsTableProps) => {
  const data = makeTripData(page)

  const { HeaderCell } = useTableHeadCell(sortData, tripSortKeyFeValues, dataCount > 0, sort)
  const TripCompletedHeader = useCallback(
    () => <HeaderCell {...{ ...tripCompletedHeaderProps, property: `finishedAt` }} />,
    [HeaderCell]
  )

  const VehicleHeader = useCallback(() => <HeaderCell {...{ ...vehicleHeaderProps, property: `vrn` }} />, [HeaderCell])

  const VehicleScoreHeader = useCallback(
    () => <HeaderCell {...{ ...vehicleScoreHeaderProps, property: `riskScore` }} />,
    [HeaderCell]
  )

  const UnsafeMilesHeader = useCallback(
    () => <HeaderCell {...{ ...unsafeMilesHeaderProps, property: `unsafeMiles` }} />,
    [HeaderCell]
  )
  const SpeedingBreakdownHeader = useCallback(
    () => <HeaderCell {...{ ...speedingBreakdownHeaderProps, property: `speedBands` }} />,
    [HeaderCell]
  )
  const SafeMilesHeader = useCallback(
    () => <HeaderCell {...{ ...safeMilesHeaderProps, property: `safeMiles` }} />,
    [HeaderCell]
  )
  const columns = useMemo<Array<Column<CompletedTripsData>>>(() => {
    let tableColumns: Array<Column<CompletedTripsData>> = [
      {
        Header: TripCompletedHeader,
        accessor: `finishedAt`,
        Cell: TripCompletedCell({
          dataTestIds: { DAY_DATE: COMPLETED_TRIPS_TABLE_DTI.DAY_DATE, TIME: COMPLETED_TRIPS_TABLE_DTI.TIME },
        }),
      },
    ]
    if (!isVehicle) {
      tableColumns = tableColumns.concat([
        {
          Header: VehicleHeader,
          accessor: `vehicle`,
          Cell: VehicleCell,
        },
        {
          Header: VehicleScoreHeader,
          accessor: `vehicleScore`,
          Cell: VehicleScoreCell,
        },
      ])
    }
    return tableColumns.concat([
      {
        Header: UnsafeMilesHeader,
        accessor: `unsafeMiles`,
      },
      {
        Header: SpeedingBreakdownHeader,
        accessor: `speedBands`,
        Cell: SpeedingBreakdownCell(),
      },
      {
        Header: SafeMilesHeader,
        accessor: `safeMiles`,
      },
      {
        Header: ``,
        accessor: `id`,
        Cell: ChevronFakeLinkCell({
          dataTestId: COMPLETED_TRIPS_TABLE_DTI.CHEVRON,
        }),
      },
    ])
  }, [
    UnsafeMilesHeader,
    isVehicle,
    SafeMilesHeader,
    SpeedingBreakdownHeader,
    TripCompletedHeader,
    VehicleHeader,
    VehicleScoreHeader,
  ])
  return (
    <>
      <TableWithPagination<CompletedTripsData>
        columns={columns}
        data={data}
        dataCount={dataCount}
        pagination={pagination}
        loading={loading}
        renderRowAsAnchor
        buildUrl={(row) => buildHrefLinkForRows(`${PATHS.INSIGHTS.BASE}/${PATHS.INSIGHTS.TRIPS}`, row.original.id)}
      />
    </>
  )
}
